// Copyright (c) Ping. All rights reserved.
//
// PEER side of the no-QR recovery peer-history hand-off (platform-agnostic; the
// web host in apps/web wires the injected deps to its BE client + SDK).
//
// When a peer recovers from backup it loses the plaintext of conversations it
// shares with us (MLS forward secrecy) and re-joins as a FRESH MLS leaf — but
// the backend's membership row and our local MLS tree still only know its OLD
// leaf, so it can neither receive new messages nor see old ones. The recovered
// device signals "I need re-admission + history" by advertising a
// `history_delivery_pubkey` on its device record. This routine, run on OUR
// device for a conversation, detects that advert and, per advertising device:
//
//   1. claims a fresh KeyPackage for the device,
//   2. `addMembers([{deviceId, keyPackage}])` — primed via the transport's
//      welcome-recipient hint so the Welcome is routed to it — re-admitting it
//      to the MLS group (the Commit updates BE membership, the Welcome lets the
//      device auto-join),
//   3. builds a recent-history bundle for the conversation, seals it to the
//      advertised delivery pubkey + signs with OUR identity secret, and POSTs it
//      to the device's history-transfer inbox,
//   4. records `${conversationId}:${deviceId}` in a LOCAL persisted admitted set.
//
// The admitted set is load-bearing: re-adding an existing MLS member duplicates
// its leaf and corrupts the tree, so we NEVER re-admit a device we've already
// recorded. We only ever admit devices that ADVERTISE a delivery key (the
// recovery signal) — never blind-add.
//
// Idempotent + best-effort: per-device errors are swallowed so one bad device
// doesn't strand the others. Rate-limited by the caller (`shouldRun`) so it runs
// at most once per conversation per app session unless the advertising-device
// set changed.
//
// The phrase-restore duplicate-signature-key rejection (recovery.md /
// 11-logout-relogin-restore.md "root cause 6") is fixed at the SDK layer, NOT
// here: a device restoring the SAME identity keeps its signing key, so RFC 9420
// leaf validation rejects an Add that duplicates the still-present dead leaf.
// The atomic, MLS-correct remove-then-add belongs in `ping-core` (spec:
// `ping-openmls-sdk/docs/specs/re-admit-device.md`) and is exposed as the
// `reAdmitDevice` conversation primitive — one implementation, mirrored to every
// binding + conformance-tested, so clients never reason about leaf indices or
// signature keys (and can't fork the behaviour per platform, per SDK hard rule
// 5). It is inherently safe: it evicts ONLY the leaf whose signature key EQUALS
// the incoming KeyPackage's — the exact dead duplicate — so a healthy sibling
// device (different signing key) can never be evicted, and no app-layer gate is
// needed. This routine calls `reAdmitDevice` when the SDK exposes it, else falls
// back to a plain `addMembers` (fresh-identity re-login is unaffected). Until the
// SDK ships the primitive, phrase-restore re-admission stays unfixed on web —
// tracked in the spec; needs the SDK release + a ping-prime version bump.

export interface RecoveredPeerAdmitDevice {
  /** base64url device id of the advertising (recovered) peer device. */
  readonly deviceId: string;
  /** base64url-nopad X25519 history-delivery pubkey the device advertised. */
  readonly historyDeliveryPubkeyB64Url: string;
}

/** Minimal MLS surface the routine needs (subset of MessagingClientLike). */
export interface RecoveredPeerAdmitClient {
  members(id: Uint8Array): Promise<ReadonlyArray<{ user_id: Uint8Array; leaf_index: number }>>;
  getConversation(id: Uint8Array): {
    addMembers(entries: Array<{ deviceId: Uint8Array; keyPackage: Uint8Array }>): Promise<void>;
    /**
     * Re-admit a device, evicting any existing leaf that duplicates the new
     * KeyPackage's signature key (the phrase-restore case — recovery.md "root
     * cause 6"). This is an SDK primitive (see
     * `ping-openmls-sdk/docs/specs/re-admit-device.md`): the atomic, MLS-correct
     * remove-then-add is owned by `ping-core` and mirrored to every binding, so
     * clients never reason about leaf indices / signature keys. Optional until
     * the SDK ships it — when absent, this routine falls back to a plain
     * `addMembers` (safe: a fresh-identity re-login is unaffected; only the
     * same-signing-key phrase-restore stays unfixed, never a wrong eviction).
     */
    reAdmitDevice?(entry: { deviceId: Uint8Array; keyPackage: Uint8Array }): Promise<void>;
  };
}

/** Transport welcome-recipient priming hint (PingTransport-shaped). */
export interface RecoveredPeerAdmitTransport {
  setWelcomeRecipients(conversationIdHex: string, recipientDeviceIdHexes: readonly string[]): void;
}

export interface EnsureRecoveredPeerDevicesAdmittedDeps {
  /** Hex conversation id of the conversation being reconciled. */
  readonly conversationIdHex: string;
  /** Live SDK client. */
  readonly client: RecoveredPeerAdmitClient;
  /** Live transport (welcome priming). May be null — admit is skipped then. */
  readonly transport: RecoveredPeerAdmitTransport | null;
  /** Our own account id (base64url) — excluded from the member sweep. */
  readonly selfAccountId: string;

  /** List the devices of `accountId` that advertise a history-delivery pubkey. */
  readonly listDevicesWithDeliveryKey: (
    accountId: string,
  ) => Promise<readonly RecoveredPeerAdmitDevice[]>;
  /** Claim ONE fresh KeyPackage for `deviceId` of `accountId`, or null if none. */
  readonly claimKeyPackageForDevice: (params: {
    accountId: string;
    deviceIdB64Url: string;
  }) => Promise<Uint8Array | null>;
  /**
   * Build, seal (to `deliveryPubkeyB64Url`, signed by our identity), and POST a
   * recent-history bundle for this conversation to `deviceId`'s inbox.
   */
  readonly sendHistoryToDevice: (params: {
    recipientDeviceIdB64Url: string;
    deliveryPubkeyB64Url: string;
    conversationIdHex: string;
  }) => Promise<void>;

  /** Read the persisted admitted-set membership for `${conversationId}:${deviceId}`. */
  readonly hasAdmitted: (key: string) => Promise<boolean>;
  /** Persist `${conversationId}:${deviceId}` into the admitted set. */
  readonly recordAdmitted: (key: string) => Promise<void>;

  /** base64url(device id) → bytes. */
  readonly b64UrlToBytes: (s: string) => Uint8Array;
  /** hex conversation id → bytes. */
  readonly hexToBytes: (hex: string) => Uint8Array;
  /** roster member user_id bytes → base64url account id (=== accountId). */
  readonly userIdToAccountId: (userId: Uint8Array) => string;

  readonly log: {
    info: (o: Record<string, unknown>) => void;
    warn: (o: Record<string, unknown>) => void;
  };
}

/**
 * Admit + history-seed every NOT-YET-ADMITTED recovered peer device that
 * advertises a delivery key for this conversation. Idempotent + best-effort.
 * Returns the number of devices admitted this pass.
 */
export async function ensureRecoveredPeerDevicesAdmitted(
  deps: EnsureRecoveredPeerDevicesAdmittedDeps,
): Promise<number> {
  const convBytes = deps.hexToBytes(deps.conversationIdHex);

  // 1) Member ACCOUNT ids from the MLS roster (base64url(user_id) === accountId),
  //    deduped, excluding self.
  let accountIds: string[];
  try {
    const roster = await deps.client.members(convBytes);
    const seen = new Set<string>();
    accountIds = [];
    for (const m of roster) {
      const acct = deps.userIdToAccountId(m.user_id);
      if (acct.length === 0 || acct === deps.selfAccountId || seen.has(acct)) continue;
      seen.add(acct);
      accountIds.push(acct);
    }
  } catch (err) {
    deps.log.warn({
      event: 'recovery.peerAdmit.membersFailed',
      conversationPrefix: deps.conversationIdHex.slice(0, 8),
      message: (err as Error).message,
    });
    return 0;
  }
  if (accountIds.length === 0) return 0;

  let admittedCount = 0;
  for (const accountId of accountIds) {
    let devices: readonly RecoveredPeerAdmitDevice[];
    try {
      devices = await deps.listDevicesWithDeliveryKey(accountId);
    } catch {
      continue; // best-effort per account
    }
    for (const device of devices) {
      // Guard 1: only devices advertising a delivery key reach here.
      // Guard 2: NEVER re-admit a device already in the admitted set — re-adding
      // an existing MLS member duplicates its leaf and corrupts the tree.
      const admitKey = `${deps.conversationIdHex}:${device.deviceId}`;
      try {
        if (await deps.hasAdmitted(admitKey)) continue;
      } catch {
        continue; // if we can't read the set, do NOT risk a duplicate add
      }
      try {
        await admitOneDevice(deps, convBytes, accountId, device, admitKey);
        admittedCount += 1;
      } catch (err) {
        // Best-effort: swallow per-device errors so one bad device (KP empty,
        // transient commit failure) doesn't strand the rest.
        deps.log.warn({
          event: 'recovery.peerAdmit.deviceFailed',
          conversationPrefix: deps.conversationIdHex.slice(0, 8),
          devicePrefix: device.deviceId.slice(0, 8),
          message: (err as Error).message,
        });
      }
    }
  }
  if (admittedCount > 0) {
    deps.log.info({
      event: 'recovery.peerAdmit.done',
      conversationPrefix: deps.conversationIdHex.slice(0, 8),
      admitted: admittedCount,
    });
  }
  return admittedCount;
}

async function admitOneDevice(
  deps: EnsureRecoveredPeerDevicesAdmittedDeps,
  convBytes: Uint8Array,
  accountId: string,
  device: RecoveredPeerAdmitDevice,
  admitKey: string,
): Promise<void> {
  if (deps.transport === null) {
    throw new Error('no transport — cannot prime Welcome recipient');
  }
  const keyPackage = await deps.claimKeyPackageForDevice({
    accountId,
    deviceIdB64Url: device.deviceId,
  });
  if (keyPackage === null) {
    throw new Error('no KeyPackage available for device');
  }

  // Prime the Welcome's `?recipient=` so the BE routes the join to this device,
  // then re-admit it to the MLS group. RECORD BEFORE the history send so that a
  // crash after a SUCCESSFUL addMembers can never lead to a duplicate-leaf
  // re-add on the next pass (the costly, unrecoverable failure). A missed
  // history send is recoverable — the device still gets live messages and the
  // peer can re-open later — a duplicate leaf is not.
  deps.transport.setWelcomeRecipients(deps.conversationIdHex, [device.deviceId]);
  const conv = deps.client.getConversation(convBytes);
  const entry = { deviceId: deps.b64UrlToBytes(device.deviceId), keyPackage };
  // Prefer the SDK's `reAdmitDevice` primitive (atomic evict-then-add for the
  // phrase-restore duplicate-signature-key case); fall back to a plain add on
  // bindings that predate it. `reAdmitDevice` evicts ONLY a leaf whose signature
  // key equals this KeyPackage's, so it is a strict superset of `addMembers` —
  // safe to prefer unconditionally.
  if (conv.reAdmitDevice !== undefined) {
    await conv.reAdmitDevice(entry);
  } else {
    await conv.addMembers([entry]);
  }
  await deps.recordAdmitted(admitKey);

  // Seal + ship recent history to the device's delivery key. Best-effort: a
  // failure here is logged by the caller but does not un-record the admit.
  await deps.sendHistoryToDevice({
    recipientDeviceIdB64Url: device.deviceId,
    deliveryPubkeyB64Url: device.historyDeliveryPubkeyB64Url,
    conversationIdHex: deps.conversationIdHex,
  });
  deps.log.info({
    event: 'recovery.peerAdmit.admitted',
    conversationPrefix: deps.conversationIdHex.slice(0, 8),
    devicePrefix: device.deviceId.slice(0, 8),
  });
}
